import keyBy from 'lodash/keyBy'
import uniq from 'lodash/uniq'

import { excludeAbortError, isAbortError } from '@app/errors/AbortError'

import { createSelector } from '@app/utils/createSelector'
import { mapObject } from '@app/utils/mapOpject'
import { mergeMaps } from '@app/utils/mergeMaps'
import { mergeV2Entity } from '@app/utils/mergeV2Entity'
import { normalizeSafe } from '@app/utils/normalizer'

import type { AnnouncementsFilter } from '@app/packages/announcementsFilter/AnnouncementsFilter'
import type { ApiAnnouncement } from '@app/packages/api/v2/types/entities'

import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'
import type { AnnouncementMeta, AnnouncementsMetaState } from '@app/store/types/announcements'

import type { PointsMapPoint } from '@app/components/PointsMap/PointsMapPoint'

const announcementsModelsSlice = createReduxSlice<{ [key: string]: ApiAnnouncement }>('announcements_models')

/** A response the viewer just left counts, and belongs to the announcement it was left on. */
export const addAnnouncementResponseSlice = (announcement_id: string, response_id: string) =>
  announcementsModelsSlice.update(state => {
    const model = state?.[announcement_id]
    if (!model) return state

    const announcement: ApiAnnouncement = {
      ...model,
      attributes: { ...model.attributes, responses_count: model.attributes.responses_count + 1 },
      relationships: {
        ...model.relationships,
        responses: { data: [...(model.relationships.responses.data || []), { type: 'announcement_responses', id: response_id }] },
      },
    }

    return { ...state, [announcement_id]: announcement }
  })

const announcementsModelsMetaSlice = createReduxSlice<{ [key: string]: AnnouncementMeta }>('announcements_models_meta')

/** An announcement that just arrived is not being updated, whatever it was doing before. */
export const resetAnnouncementsMetaSlice = (payload: unknown) =>
  announcementsModelsMetaSlice.update(state => {
    const { announcements } = normalizeSafe<ApiAnnouncement>(payload)
    if (!announcements) return state
    return { ...state, ...mapObject(announcements, (): AnnouncementMeta => ({ updating: false, error: null })) }
  })

/** Anything acting on one announcement marks it busy, so its controls can lock. */
export const startAnnouncementUpdateSlice = (id: string) => announcementsModelsMetaSlice.update(state => ({ ...state, [id]: { updating: true, error: null } }))

export const finishAnnouncementUpdateSlice = (id: string) =>
  announcementsModelsMetaSlice.update(state => ({ ...state, [id]: { updating: false, error: null } }))

export const failAnnouncementUpdateSlice = (id: string, error: Error) =>
  announcementsModelsMetaSlice.update(state => ({ ...state, [id]: { updating: false, error: excludeAbortError(error) } }))

const announcementsListSlice = createReduxSlice<string[]>('announcements_list')
const announcementsHiddenSlice = createReduxSlice<{ [key: string]: boolean }>('announcements_hidden')
const announcementsMapSlice = createReduxSlice<{ [key: string]: PointsMapPoint }>('announcements_map')
const announcementsMetaSlice = createReduxSlice<AnnouncementsMetaState>('announcements_meta')
const announcementsCountSlice = createReduxSlice<AnnouncementsCount>('announcements_count')

/** A cursor page appends; a fresh search replaces. */
export const setAnnouncementsListSlice = (ids: string[], { append }: { append: boolean }) =>
  announcementsListSlice.update(state => (append ? uniq([...(state ?? EMPTY_LIST), ...ids]) : ids))

export const setAnnouncementHiddenSlice = (id: string, hidden: boolean) => announcementsHiddenSlice.update(state => ({ ...state, [id]: hidden }))

export const setAnnouncementsMapSlice = (points: PointsMapPoint[], { initial }: { initial: boolean }) =>
  announcementsMapSlice.update(state => {
    const obj = keyBy(points, p => p.id)
    return initial ? obj : mergeMaps(state ?? {}, obj)
  })

export const startAnnouncementsLoadSlice = () => announcementsMetaSlice.update(state => ({ ...(state ?? DEFAULT_META), loading: true }))

export const failAnnouncementsLoadSlice = (error: Error) =>
  announcementsMetaSlice.update(state => (isAbortError(error) ? state : { ...(state ?? DEFAULT_META), loading: false }))

/** A search page carries the cursor to continue from, and the total the counter shows. */
export const setAnnouncementsSearchMetaSlice = (meta: { current_page: number; total_pages: number; total: number; cursor: string }) =>
  createThunk(dispatch => {
    dispatch(
      announcementsMetaSlice.update(state => ({
        ...(state ?? DEFAULT_META),
        loading: false,
        cursor: meta.current_page >= meta.total_pages ? null : meta.cursor,
      }))
    )
    dispatch(announcementsCountSlice.update(state => ({ ...(state ?? DEFAULT_COUNT), value: meta.total, fetched: true })))
  })

export const setAnnouncementsUpcomingMetaSlice = (total: number) =>
  announcementsMetaSlice.update(state => ({ ...(state ?? DEFAULT_META), page: 1, total, total_pages: 1, loading: false }))

export const startAnnouncementsCountSlice = () => announcementsCountSlice.update(state => ({ ...(state ?? DEFAULT_COUNT), loading: true, error: null }))

export const setAnnouncementsCountSlice = (value: number) => announcementsCountSlice.set({ fetched: true, loading: false, value, error: null })

export const failAnnouncementsCountSlice = (error: Error) =>
  announcementsCountSlice.update(state => ({ ...(state ?? DEFAULT_COUNT), loading: false, error: excludeAbortError(error) }))

/** Any response can carry announcements along; each one merges over what we hold. */
export const updateAnnouncementsSlice = (payload: unknown) =>
  announcementsModelsSlice.update(state => {
    const { announcements } = normalizeSafe<ApiAnnouncement>(payload)
    if (!announcements) return state
    return { ...state, ...mapObject(announcements, a => mergeV2Entity(state?.[a.id], a)) }
  })

export const announcementsModelsSelector = createSelector([announcementsModelsSlice.selector], models => models ?? {})
export const announcementsModelsMetaSelector = createSelector([announcementsModelsMetaSlice.selector], meta => meta ?? {})
export const announcementsRawIdsListSelector = createSelector([announcementsListSlice.selector], list => list ?? EMPTY_LIST)
export const announcementsHiddenSelector = createSelector([announcementsHiddenSlice.selector], hidden => hidden ?? {})
export const announcementsMapPointsSelector = createSelector([announcementsMapSlice.selector], points => points ?? {})
export const announcementsMetaSelector = createSelector([announcementsMetaSlice.selector], meta => meta ?? DEFAULT_META)
export const announcementsCountSelector = createSelector([announcementsCountSlice.selector], count => count ?? DEFAULT_COUNT)

export const setAnnouncementFilterSlice = (filter: AnnouncementsFilter) => announcementsMetaSlice.update(state => ({ ...(state ?? DEFAULT_META), filter }))

type AnnouncementsCount = {
  fetched: boolean
  loading: boolean
  value: number
  error: Error | null
}

const EMPTY_LIST: string[] = []

const DEFAULT_META: AnnouncementsMetaState = { loading: false, filter: {} }

const DEFAULT_COUNT: AnnouncementsCount = {
  fetched: false,
  loading: false,
  value: 0,
  error: null,
}
